import axios from "axios";
import {
  buildSignedDocumentFormData,
  multipartRequestConfig,
  SignedDocumentKind,
} from "../../signed-document";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { useCallback, useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DCSQueryClient } from "../../../../../queryClient";
import { UserRoles } from "../../../../../constants/userRoles";
import { useToast } from "../../../../../Providers/ToastProvider";
import { useUser } from "../../../../../keycloak";
import { requests } from "../../../../../services/http-common";
import { ContractStatusEnum } from "../../Contracts.model";
import { ConfirmationModal } from "../../../../../components/ConfirmationModal/ConfirmationModal";
import { AttachSignedDocumentDialog } from "./AttachSignedDocumentDialog";
import { ManageJobDialog } from "./ManageJobDialog";

// A long attachment name would otherwise stretch the actions menu; keep the
// extension visible by truncating through the middle.
const truncateFileName = (fileName: string, max = 30) =>
  fileName.length <= max
    ? fileName
    : `${fileName.slice(0, max - 13)}…${fileName.slice(-12)}`;

export const Actions = (rowData: {
  id: string;
  lead: { id: string };
  hasJob: boolean;
  jobId?: number | null;
  status: ContractStatusEnum;
  pdfPath?: string;
  sendEstimateStatus: boolean;
  sendStatus: boolean;
  isDisabled: boolean;
  envelopeId?: string;
  hasSignedDocument?: boolean;
  signedDocuments?: { id: string; fileName: string; path: string; kind?: "signed" | "attachment" }[];
  signedDate?: string;
}) => {
  const menu = useRef(null);
  const navigate = useNavigate();
  const { show } = useToast();
  const user: any = useUser();
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [showAttachDialog, setShowAttachDialog] = useState<boolean>(false);
  const [showManageJob, setShowManageJob] = useState<boolean>(false);

  const jobQuery = useQuery({
    queryKey: ["contract-job", rowData.jobId],
    queryFn: () => requests.get(`/job/${rowData.jobId}`),
    enabled: showManageJob && !!rowData.jobId,
  });

  const deleteJob = useMutation({
    mutationFn: () => requests.delete(`/job/${rowData.jobId}`),
    onSuccess: () => {
      show({ detail: "Job deleted. This contract can be scheduled again." });
      setShowManageJob(false);
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Failed to delete job",
      });
    },
  });

  const deleteJobSchedule = useMutation({
    mutationFn: (scheduleId: number) => requests.delete(`/job/jobSchedule/${scheduleId}`),
    onSuccess: () => {
      show({ detail: "Schedule deleted." });
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-job", rowData.jobId] });
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Failed to delete schedule",
      });
    },
  });

  const deleteContract = useMutation({
    mutationFn: () => {
      return requests.delete(`/contract/${rowData.id}`);
    },
    onSuccess: () => {
      show({ detail: "The contract was deleted successfully !" });
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const sendForSignature = useMutation({
    mutationFn: () => {
      return requests.post(`/contract/${rowData.id}/send-for-signature`, {});
    },
    onSuccess: () => {
      show({ detail: "Contract sent for signature successfully!" });
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong",
      });
    },
  });

  const handleDownload = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DSC_SERVICE_URL}/contract/${rowData.id}/download`,
        { responseType: 'blob' },
      );
      const contentType = response.headers['content-type'] || 'application/zip';
      const url = URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract-${rowData.id}-signed.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      show({
        severity: 'error',
        detail: 'Failed to download signed document',
      });
    }
  }, [rowData.id, show]);

  const attachDocuments = useMutation({
    mutationFn: ({ files, signedDate, kind }: { files: File[]; signedDate: Date | null; kind: string }) => {
      return requests.post(
        `/contract/${rowData.id}/signed-document`,
        buildSignedDocumentFormData(files, signedDate, kind as SignedDocumentKind),
        multipartRequestConfig
      );
    },
    onSuccess: () => {
      show({ detail: "Signed document(s) attached." });
      setShowAttachDialog(false);
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Failed to attach documents",
      });
    },
  });

  const removeDocument = useMutation({
    mutationFn: (documentId: string) =>
      requests.delete(`/contract/${rowData.id}/signed-document/${documentId}`),
    onSuccess: () => {
      show({ detail: "Document removed." });
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Failed to remove document",
      });
    },
  });

  const completeContract = useMutation({
    mutationFn: () => requests.post(`/contract/${rowData.id}/complete`, {}),
    onSuccess: () => {
      show({ detail: "Contract marked as completed." });
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail:
          error.response?.data?.error?.message || "Failed to complete contract",
      });
    },
  });

  const handleViewDocument = useCallback(async (documentId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DSC_SERVICE_URL}/contract/${rowData.id}/signed-document/${documentId}`,
        { responseType: 'blob' },
      );
      const contentType = response.headers['content-type'] || 'application/pdf';
      const url = URL.createObjectURL(new Blob([response.data], { type: contentType }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      show({
        severity: 'error',
        detail: 'Failed to open signed document',
      });
    }
  }, [rowData.id, show]);

  const handleOpenAirslate = useCallback(async () => {
    const openedWindow = window.open('about:blank', '_blank');

    if (openedWindow) {
      openedWindow.opener = null;
    }

    try {
      const response = await requests.get(`/contract/${rowData.id}/airslate-workflow-link`);

      if (openedWindow) {
        openedWindow.location.href = response.url;
      } else {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      openedWindow?.close();
      show({
        severity: 'error',
        detail: error.response?.data?.error?.message || 'Failed to open Altaflow workflow',
      });
    }
  }, [rowData.id, show]);

  const items = useMemo(() => {
    let _items: any = [];

    if (user?.roles.dcs_client.includes(UserRoles.CONTRACT_VIEW)) {
      _items.push({
        label: "View",
        command: () => {
          navigate(`/sales/contract/${rowData.id}/${rowData.lead.id}/details`);
        },
      });
    }

    if (
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_UPDATE) &&
      (rowData.status === ContractStatusEnum.Pending ||
        rowData.status === ContractStatusEnum.Draft)
    ) {
      _items.push({
        label: "Edit",
        command: () => {
          navigate(
            `/sales/contract/${rowData.id}/${rowData.lead.id}/details/edit`,
          );
        },
      });
    }

    if (user?.roles.dcs_client.includes(UserRoles.CONTRACT_DELETE)) {
      _items.push({
        label: "Delete",
        command: () => {
          setShowDeletePopup(true);
        },
      });
    }

    if (
      (rowData.status === ContractStatusEnum.Sent ||
        rowData.status === ContractStatusEnum.Completed) &&
      rowData.sendEstimateStatus &&
      !rowData.hasJob &&
      user?.roles.dcs_client.includes(UserRoles.JOB_CREATE)
    ) {
      _items.push({
        label: "Create job & schedule",
        command: () => {
          navigate(`/sales/contract/create/job/${rowData.lead.id}`);
        },
      });
    }

    if (
      rowData.status === ContractStatusEnum.Approved &&
      !rowData.hasSignedDocument &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_UPDATE)
    ) {
      _items.push({
        label: "Send & Sign",
        command: () => {
          sendForSignature.mutate();
        },
      });
    }

    if (
      rowData.status === ContractStatusEnum.Completed &&
      rowData.envelopeId &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_VIEW)
    ) {
      _items.push({
        label: 'Download Signed',
        command: () => handleDownload(),
      });
    }

    if (
      (rowData.status === ContractStatusEnum.Sent ||
        rowData.status === ContractStatusEnum.Completed) &&
      rowData.envelopeId &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_VIEW)
    ) {
      _items.push({
        label: 'Open Altaflow',
        command: () => handleOpenAirslate(),
      });
    }

    if (
      rowData.signedDocuments?.length &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_VIEW)
    ) {
      _items.push({
        label: "View documents",
        items: rowData.signedDocuments.map((doc) => ({
          label: truncateFileName(doc.fileName),
          command: () => handleViewDocument(doc.id),
        })),
      });
    }

    if (
      !rowData.envelopeId &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_UPDATE)
    ) {
      _items.push({
        label: rowData.signedDocuments?.length ? "Manage signed documents" : "Attach signed documents",
        command: () => setShowAttachDialog(true),
      });
    }

    if (
      rowData.hasSignedDocument &&
      rowData.status !== ContractStatusEnum.Completed &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_UPDATE)
    ) {
      _items.push({
        label: 'Mark as completed',
        command: () => completeContract.mutate(),
      });
    }

    if (
      rowData.hasJob &&
      rowData.jobId &&
      user?.roles.dcs_client.includes(UserRoles.JOB_VIEW)
    ) {
      _items.push({
        label: "Manage job & schedules",
        command: () => setShowManageJob(true),
      });
    }

    if (
      rowData.hasJob &&
      rowData.jobId &&
      user?.roles.dcs_client.includes(UserRoles.JOB_UPDATE)
    ) {
      _items.push({
        label: "Edit day pattern",
        command: () => {
          navigate(
            `/sales/contract/edit/job/${rowData.lead.id}/${rowData.jobId}`,
          );
        },
      });
    }

    return _items;
  }, [
    rowData.id,
    rowData.lead.id,
    rowData.status,
    rowData.sendEstimateStatus,
    rowData.hasJob,
    rowData.jobId,
    rowData.envelopeId,
    rowData.hasSignedDocument,
    rowData.signedDocuments,
    completeContract,
    removeDocument,
    sendForSignature,
    navigate,
    user,
    handleDownload,
    handleOpenAirslate,
    handleViewDocument,
  ]);

  return (
    <>
      <Menu
        model={items}
        popup
        ref={menu}
        id="popup_menu"
        style={{ width: "16rem" }}
      />
      <ConfirmationModal
        visible={showDeletePopup}
        onHide={() => setShowDeletePopup(false)}
        accept={() => deleteContract.mutate()}
        reject={() => setShowDeletePopup(false)}
      />
      <AttachSignedDocumentDialog
        visible={showAttachDialog}
        submitting={attachDocuments.isLoading}
        removing={removeDocument.isLoading}
        documents={rowData.signedDocuments ?? []}
        onCancel={() => setShowAttachDialog(false)}
        onSubmit={(files, signedDate, kind) => attachDocuments.mutate({ files, signedDate, kind })}
        onRemove={(documentId) => removeDocument.mutate(documentId)}
      />
      <ManageJobDialog
        visible={showManageJob}
        job={jobQuery.data}
        isLoading={jobQuery.isLoading}
        canDelete={!!user?.roles.dcs_client.includes(UserRoles.JOB_DELETE)}
        deleting={deleteJob.isLoading || deleteJobSchedule.isLoading}
        onDeleteSchedule={(scheduleId) => deleteJobSchedule.mutate(scheduleId)}
        onDeleteJob={() => deleteJob.mutate()}
        onHide={() => setShowManageJob(false)}
      />
      <Button
        icon="pi pi-ellipsis-v"
        //@ts-ignore
        onClick={(event) => menu.current.toggle(event)}
        aria-controls="popup_menu"
        aria-haspopup
        className="p-button-rounded p-button-text"
        disabled={!items.length || rowData.isDisabled}
      />
    </>
  );
};
